import { alpha, Box, Stack, Typography } from '@mui/material';
import { LockOutlined, ShieldOutlined } from '@mui/icons-material';
import React, { useMemo } from 'react';
import useEkko from 'src/Common/Providers/EkkoProvider';
import { ChangePasswordForm } from '../../ChangePasswordForm';
import { SettingsSection } from '../SettingsSection';

const IconBadge = ({ children }: { children: React.ReactNode }): React.JSX.Element => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 56,
      height: 56,
      borderRadius: '50%',
      color: 'primary.main',
      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
      '& .MuiSvgIcon-root': { fontSize: 28 },
    }}
  >
    {children}
  </Box>
);

export const SecuritySection = (): React.JSX.Element => {
  const { config } = useEkko();

  const isJwtAuth = useMemo(() => config.api.authMechanism === 'jwt', [config.api.authMechanism]);

  return (
    <SettingsSection title={'Security'} description={'Manage your account password.'}>
      {isJwtAuth ? (
        <Box
          sx={{
            maxWidth: 460,
            mx: 'auto',
            p: { xs: 3, sm: 4 },
            borderRadius: 1,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Stack direction={'column'} spacing={1} alignItems={'center'} sx={{ mb: 3 }}>
            <IconBadge>
              <LockOutlined />
            </IconBadge>
            <Typography variant={'h6'} fontWeight={700}>
              Change password
            </Typography>
            <Typography variant={'body2'} color={'text.secondary'} textAlign={'center'}>
              Pick a strong password you don&apos;t reuse anywhere else.
            </Typography>
          </Stack>
          <ChangePasswordForm />
        </Box>
      ) : (
        <Stack direction={'column'} spacing={1.5} alignItems={'center'} sx={{ py: 4, textAlign: 'center' }}>
          <IconBadge>
            <ShieldOutlined />
          </IconBadge>
          <Typography variant={'h6'} fontWeight={700}>
            Single sign-on
          </Typography>
          <Typography variant={'body2'} color={'text.secondary'} sx={{ maxWidth: 420 }}>
            Your password is managed by your identity provider and can&apos;t be changed here.
          </Typography>
        </Stack>
      )}
    </SettingsSection>
  );
};
